// Manual account-portal redirect helpers, for cases @platform/wiservice's
// automatic auth-error redirect can't see (e.g. status:0 + verified:false).
// Builds the SAME portal URL shape the package does — they MUST stay aligned.

import {
  Constants,
  APPNAME,
  APPFSM_WORKFLOW_NEW,
  APPFSM_BLOCK_LOGIN,
  APPFSM_LOGOUT,
  LOGIN_FLOW_VERSION_NEW,
  WITHINGS_EMAILS,
} from '@/config/constants';

// ── URL builders ────────────────────────────────────────────────────────────

function buildReturnUrl(extraParams: Record<string, string> = {}): string {
  const currentQuery = new URLSearchParams(window.location.search);
  Object.entries(extraParams).forEach(([key, value]) => {
    currentQuery.set(key, value);
  });
  const queryStr = currentQuery.toString();
  return `${window.location.origin}${window.location.pathname}${queryStr ? `?${queryStr}` : ''}`;
}

function getWorkflowUrl(workflow: string, block: string): string {
  return `${Constants.ACCOUNT_URL}${workflow}/${block}`;
}

/**
 * Hard-redirect the browser to the Withings account portal login flow.
 *
 * Use ONLY for applicative cases the package can't decide on its own (e.g.
 * `session.isverified` says verified=false with status:0). For HTTP auth
 * errors (103/239/250/501) the package handles the redirect — don't call
 * this from a wiservice catch block.
 *
 * @param params  Extra query params to preserve on the return URL.
 * @param block   Workflow block; default 'login'. Pass 'create_user' to
 *                deep-link into account creation.
 */
export function redirectToAccount(
  params: Record<string, string> = {},
  block: string = APPFSM_BLOCK_LOGIN,
): void {
  const portal = getWorkflowUrl(APPFSM_WORKFLOW_NEW, block);
  const returnUrl = encodeURIComponent(buildReturnUrl(params));
  window.location.href = `${portal}?logver=${LOGIN_FLOW_VERSION_NEW}&appname=${APPNAME}&r=${returnUrl}`;
}

/**
 * The URL to navigate to for logging out and returning to the app's root.
 * Useful as the `href` of a logout link. Layout chrome can render it as a
 * static `<a>` for keyboard / right-click accessibility.
 */
export function getLogoutUrl(): string {
  const portal = `${Constants.ACCOUNT_URL}${APPFSM_LOGOUT}`;
  const returnUrl = encodeURIComponent(`${window.location.origin}/`);
  return `${portal}?logver=${LOGIN_FLOW_VERSION_NEW}&appname=${APPNAME}&r=${returnUrl}`;
}

/**
 * Imperative logout: clear local + session storage, redirect to the portal
 * logout URL. The portal kills the cookie and bounces back to root.
 *
 * Used as the fallback when `session.extend` fails with an unexpected
 * status the package didn't redirect on (defensive — rare in practice).
 */
export function logout(): void {
  localStorage.clear();
  sessionStorage.clear();
  window.location.href = getLogoutUrl();
}

// The keep-alive schedule deliberately lives in logic/sessionKeepAlive.ts and
// is driven by the TTL apiv2 grants on `session.extend` — NOT by the
// `session_created` cookie, which the portal only sets for the wpc/care
// appnames (routes/login.php) and so never exists for wrpm/clinic.

// ── Misc helpers ────────────────────────────────────────────────────────────

/**
 * True when the email matches a Withings staff domain. Used for in-app
 * staff-only badges or features. Informational, not a security gate.
 */
export function checkIsWithinger(email: string | null | undefined): boolean {
  if (!email) return false;
  return WITHINGS_EMAILS.some((domain) => email.includes(domain));
}
